import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { dukaElements } from "../../../api/Duka/api";
import { X } from "lucide-react";

const DukaStockForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(
    initialData
      ? {
          duka: initialData.duka ? initialData.duka.toString() : "",
          product: initialData.product ? initialData.product.toString() : "",
          available_quantity: initialData.available_quantity
            ? initialData.available_quantity.toString()
            : "",
        }
      : {
          duka: "",
          product: "",
          available_quantity: "",
        }
  );

  const [dukas, setDukas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [errors, setErrors] = useState({
    duka: "",
    product: "",
    available_quantity: "",
  });

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const dukaData = await dukaElements("dukas");
        const productData = await dukaElements('products');
        setDukas(dukaData?.data);
        setProducts(productData?.data);
        if (initialData) {
          setFormData({
            duka: initialData.duka ? initialData.duka.toString() : "",
            product: initialData.product ? initialData.product.toString() : "",
            available_quantity: initialData.available_quantity
              ? initialData.available_quantity.toString()
              : "",
          });
        }
      } catch (err) {
        console.error("Error loading options:", err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [initialData]);

  const validateForm = () => {
    let newErrors = {
      duka: "",
      product: "",
      available_quantity: "",
    };
    let isValid = true;

    if (!formData.duka || formData.duka.trim() === "") {
      newErrors.duka = "Duka is required.";
      isValid = false;
    }
    if (!formData.product || formData.product.trim() === "") {
      newErrors.product = "Product is required.";
      isValid = false;
    }
    if (formData.available_quantity === "" || isNaN(formData.available_quantity)) {
      newErrors.available_quantity = "Enter a valid number.";
      isValid = false;
    } else if (parseInt(formData.available_quantity, 10) < 0) {
      newErrors.available_quantity = "Available quantity cannot be negative.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        duka: formData.duka,
        product: formData.product,
        available_quantity: parseInt(formData.available_quantity, 10),
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          fontSize: 18,
          fontWeight: 500,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {initialData ? "Edit Duka Stock" : "Add New Duka Stock"}
        <IconButton onClick={onCancel} size="small" sx={{ color: "text.secondary" }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth required error={Boolean(errors.duka)}>
              <InputLabel id="duka-label">Duka</InputLabel>
              <Select
                labelId="duka-label"
                value={formData.duka}
                onChange={(e) =>
                  setFormData({ ...formData, duka: e.target.value })
                }
                label="Duka"
                disabled={loadingOptions}
              >
                {dukas.map((duka) => (
                  <MenuItem key={duka.id} value={duka.id.toString()}>
                    {duka.code} | {duka.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.duka && <FormHelperText>{errors.duka}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required error={Boolean(errors.product)}>
              <InputLabel id="product-label">Product</InputLabel>
              <Select
                labelId="product-label"
                value={formData.product}
                onChange={(e) =>
                  setFormData({ ...formData, product: e.target.value })
                }
                label="Product"
                disabled={loadingOptions}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id.toString()}>
                    {product.code} | {product.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.product && (
                <FormHelperText>{errors.product}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Available Quantity"
              type="number"
              value={formData.available_quantity}
              onChange={(e) =>
                setFormData({ ...formData, available_quantity: e.target.value })
              }
              required
              error={Boolean(errors.available_quantity)}
              helperText={errors.available_quantity}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: "#e0e0e0",
                  color: "#000",
                  "&:hover": { bgcolor: "#d5d5d5" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loadingOptions}
                sx={{
                  bgcolor: "#00694B",
                  color: "#fff",
                  "&:hover": { bgcolor: "#005039" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                {initialData ? "Update" : "Create"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default DukaStockForm;
